import { createAsyncThunk } from "@reduxjs/toolkit";

import { apolloClient } from "js/apollo/apollo-client";

import {
    DENY_PHOTO_UPLOADS,
    VISUALLY_PUBLISH_PHOTO_UPLOADS,
    FULLY_PUBLISH_PHOTO_UPLOADS
} from "../../mutations";

import { REDUCERS } from "../../constants";
import { messagesSlice } from "frontend/components/fort/mod/photos/slices/messagesSlice.js";
import { animationsSlice } from "frontend/components/fort/mod/photos/slices/animationsSlice.js";
import { ANIMATIONS } from "frontend/components/fort/mod/photos/constants.js";
import {
    batchesSlice,
    BatchThunks
} from "frontend/components/fort/mod/photos/slices/batchesSlice";
import { fetchUploadsInBatch } from "frontend/components/fort/mod/photos/slices/uploadsSlice/index.js";
import { isEditableCheck } from "frontend/components/fort/mod/photos/slices/batchesSlice/scripts/isEditableCheck.js";

// debugging
const DEBUG = false;
const DEBUG_SHOW_SUCCESS = true;
import { mockApiCalls } from "./mockApiCalls.js";

export const API_TITLES = {
    DENY_PHOTO_UPLOADS: "Deny",
    VISUALLY_PUBLISH_PHOTO_UPLOADS: "Viusally Approve",
    FULLY_PUBLISH_PHOTO_UPLOADS: "Fully Publish"
};

/* -------------------------------------------------------------------------- */
/*                                  THUNKS                                    */
/* -------------------------------------------------------------------------- */
export const deny = createAsyncThunk(
    `${REDUCERS.UPLOAD_APPROVAL}/deny`,
    async (props, thunkAPI = {}) => {
        return await clientCall({
            title: API_TITLES.DENY_PHOTO_UPLOADS,
            mutation: DENY_PHOTO_UPLOADS,
            thunkAPI,
            ...props
        });
    }
);

export const visuallyPublish = createAsyncThunk(
    `${REDUCERS.UPLOAD_APPROVAL}/visuallyPublish`,
    async (props, thunkAPI = {}) => {
        return await clientCall({
            title: API_TITLES.VISUALLY_PUBLISH_PHOTO_UPLOADS,
            mutation: VISUALLY_PUBLISH_PHOTO_UPLOADS,
            thunkAPI,
            ...props
        });
    }
);

export const fullyPublish = createAsyncThunk(
    `${REDUCERS.UPLOAD_APPROVAL}/fullyPublish`,
    async (props, thunkAPI) => {
        return await clientCall({
            title: API_TITLES.FULLY_PUBLISH_PHOTO_UPLOADS,
            mutation: FULLY_PUBLISH_PHOTO_UPLOADS,
            thunkAPI,
            ...props
        });
    }
);

/* -------------------------------------------------------------------------- */
/*                              Client Call                                   */
/* -------------------------------------------------------------------------- */
const clientCall = async props => {
    const { title, mutation, photoUploadIds = [] } = props;

    let resp;

    if (DEBUG) {
        resp = await mockApiCalls({
            title,
            ok: DEBUG_SHOW_SUCCESS,
            photoUploadIds,
            timer: 3000
        }).response();
    } else {
        resp = await apolloClient.mutate({
            mutation,
            variables: { photoUploadIds }
        });
    }

    const { payload } = resp.data;

    handleResp({ ...props, apiPayload: payload });

    // returns so uploadApprovalSlice can update state
    return payload;
};

/* -------------------------------------------------------------------------- */
/*                             Handle Response                                */
/* -------------------------------------------------------------------------- */
const handleResp = ({
    title,
    thunkAPI,
    selectedBatchObj,
    allUploadsSelectedInBatch,
    currentQueue,
    currentUser,
    apiPayload
}) => {
    const { dispatch } = thunkAPI;

    // Handle Errors, MessgeStack, Trigger Animation
    const { ok, errors: message } = apiPayload; // { ok, errors, photoUploads }

    if (!ok) {
        const errorMessage = `${title} Error (BatchID: ${selectedBatchObj.id}): <p>${message}.</p> <p>Please refresh the browser and try again.</p>`;
        if (DEBUG) console.log({ errorMessage });

        dispatch(
            messagesSlice.actions.addMessage({
                text: errorMessage,
                timed: false,
                timeout: null,
                infoType: "error"
            })
        );
        // restore to previous editablility state on Batch
        dispatch(
            batchesSlice.actions.updateOne({
                id: selectedBatchObj.id,
                changes: {
                    isEditable: isEditableCheck({
                        batch: selectedBatchObj,
                        currentQueue,
                        currentUser
                    }),
                    isFaded: false
                }
            })
        );
    } else {
        if (allUploadsSelectedInBatch) {
            dispatch(
                animationsSlice.actions.addAnimation({
                    id: ANIMATIONS.BATCH_REMOVE,
                    payload: {
                        batchId: selectedBatchObj.id
                    }
                })
            );
        } else {
            // if user only selected a subset of uploads, we need to refresh the uploads in the batch
            // refetch batches :: this takes care of any changed upload counts
            dispatch(
                BatchThunks.fetchBatches({
                    moderationType: "VISUAL",
                    currentUser
                })
            );
            // refetch uploads :: this takes care of the actual filtering based on views (e.g. Visual Mod)
            dispatch(fetchUploadsInBatch(selectedBatchObj.id));
        }

        // Product wants a more generic success message "Visual review complete" for all
        // adding here for debugging if needed.
        const successMessage = `${title} Success (BatchID: ${selectedBatchObj.id})`;
        if (DEBUG) console.log({ successMessage });

        dispatch(
            messagesSlice.actions.addMessage({
                text: `Visual review complete`,
                timed: true,
                timeout: 5000,
                infoType: "success"
            })
        );
    }
};
